/**
 * Admin Article Form (Create + Edit)
 *
 * Routes:
 * - /admin/articles/new
 * - /admin/articles/:id
 *
 * Uses backend admin endpoints:
 * - POST   /api/articles
 * - GET    /api/articles/admin/:id
 * - PUT    /api/articles/admin/:id
 * - DELETE /api/articles/admin/:id
 * - PUT    /api/articles/admin/:id/publish
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  HiSave,
  HiTrash,
  HiEye,
  HiEyeOff,
  HiRefresh,
} from 'react-icons/hi';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import { adminFetch, adminHeaders } from '../lib/adminApi';

type Article = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content_md: string;
  keywords: string[] | null;
  city: string | null;
  status: 'draft' | 'published';
  published_at: string | null;
};

type Tab = 'edit' | 'preview';

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function ArticleForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('edit');

  // Form fields
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [city, setCity] = useState('');
  const [keywords, setKeywords] = useState(''); // comma separated
  const [contentMd, setContentMd] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [publishedAt, setPublishedAt] = useState<string | null>(null);

  const keywordArray = useMemo(() => {
    return keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
  }, [keywords]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`/api/articles/admin/${id}`);
      if (res.status === 404) {
        setError('Article not found');
        return;
      }
      if (!res.ok) throw new Error('Failed to load article');
      const data = await res.json();
      const a: Article | null = data.article || null;
      if (!a) {
        setError('Article not found');
        return;
      }
      setTitle(a.title || '');
      setSlug(a.slug || '');
      setExcerpt(a.excerpt || '');
      setCity(a.city || '');
      setKeywords((a.keywords || []).join(', '));
      setContentMd(a.content_md || '');
      setStatus(a.status);
      setPublishedAt(a.published_at);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!title.trim() || !slug.trim() || !contentMd.trim()) {
        setError('Title, slug, and content are required.');
        return;
      }

      if (isNew) {
        const res = await adminFetch('/api/articles', {
          method: 'POST',
          headers: adminHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            title,
            slug,
            excerpt: excerpt || null,
            contentMd,
            keywords: keywordArray,
            city: city || null,
            status,
            publishedAt,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to create article');
        }
        const data = await res.json();
        const created = data.article;
        if (created?.id) {
          navigate(`/admin/articles/${created.id}`);
        } else {
          navigate('/admin/articles');
        }
        return;
      }

      const res = await adminFetch(`/api/articles/admin/${id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          title,
          slug,
          excerpt: excerpt || null,
          contentMd,
          keywords: keywordArray,
          city: city || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update article');
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (!id) return;
    const nextPublish = status !== 'published';
    try {
      const res = await adminFetch(`/api/articles/admin/${id}/publish`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ publish: nextPublish }),
      });
      if (!res.ok) throw new Error('Failed to update publish status');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update publish status');
    }
  };

  const deleteArticle = async () => {
    if (!id) return;
    const ok = window.confirm('Delete this article? This cannot be undone.');
    if (!ok) return;
    try {
      const res = await adminFetch(`/api/articles/admin/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete article');
      navigate('/admin/articles');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete article');
    }
  };

  const autoSlug = () => {
    const next = slugify(title);
    setSlug(next);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Link to="/admin/articles" className="text-purple-700 hover:text-purple-800 font-medium">
              ← Back to Articles
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 mt-2">
              {isNew ? 'New Article' : 'Edit Article'}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {!isNew && (
              <button
                onClick={togglePublish}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium"
                title={status === 'published' ? 'Unpublish' : 'Publish'}
              >
                {status === 'published' ? <HiEyeOff className="w-5 h-5" /> : <HiEye className="w-5 h-5" />}
                {status === 'published' ? 'Unpublish' : 'Publish'}
              </button>
            )}

            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-semibold"
            >
              <HiSave className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save'}
            </button>

            {!isNew && (
              <button
                onClick={deleteArticle}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 font-medium"
              >
                <HiTrash className="w-5 h-5" />
                Delete
              </button>
            )}
          </div>
        </div>

        {!isNew && (
          <div className="text-sm text-gray-600">
            Status: <span className="font-medium text-gray-900">{status === 'published' ? 'Published' : 'Draft'}</span>
            {status === 'published' && publishedAt && (
              <>
                {' '}
                · Published: <span className="font-medium text-gray-900">{new Date(publishedAt).toLocaleString()}</span>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-2xl p-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-600 flex items-center gap-2">
          <HiRefresh className="w-5 h-5 animate-spin" />
          Loading article...
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="e.g., How to order a custom cake in Barrie"
            />
          </div>

          {/* Slug */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Slug</label>
              <button
                type="button"
                onClick={autoSlug}
                className="text-sm text-purple-700 hover:text-purple-800 font-medium"
              >
                Auto-generate
              </button>
            </div>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
              placeholder="e.g., custom-cake-ordering-barrie"
            />
            <div className="text-xs text-gray-500 mt-1">
              Public URL: <span className="font-mono">/guides/{slug || 'your-slug'}</span>
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt (SEO description)</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              rows={3}
              placeholder="Short summary used for previews and meta description."
            />
          </div>

          {/* City + Keywords */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City (optional)</label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Barrie"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Keywords (comma-separated)</label>
              <input
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="custom cakes, barrie, bakery"
              />
            </div>
          </div>

          {/* Draft/Published */}
          {isNew && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Create as</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
                className="border border-gray-300 rounded-xl px-3 py-2 bg-white text-gray-900"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          )}

          {/* Editor Tabs */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => setTab('edit')}
                className={
                  tab === 'edit'
                    ? 'px-3 py-2 rounded-xl bg-purple-600 text-white font-semibold'
                    : 'px-3 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200'
                }
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setTab('preview')}
                className={
                  tab === 'preview'
                    ? 'px-3 py-2 rounded-xl bg-purple-600 text-white font-semibold'
                    : 'px-3 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200'
                }
              >
                Preview
              </button>
            </div>

            {tab === 'edit' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content (Markdown)</label>
                <textarea
                  value={contentMd}
                  onChange={(e) => setContentMd(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                  rows={14}
                  placeholder="# Heading\n\nWrite your guide here..."
                />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                <MarkdownRenderer markdown={contentMd} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


